import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { analytics } from "../../analytics/analytics";
import { api } from "../../api/client";
import { Button, PageHeader, Tag } from "../../components/ui";
import { t } from "../../i18n";
import { useAppStore } from "../../state/appStore";
export function ReservePage() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { authenticated, draft, result } = useAppStore();
  const plan = result?.plans.find((item) => item.id === planId);
  const [name, setName] = useState("");
  const mutation = useMutation({
    mutationFn: () => api.reservePlan(planId!),
    onSuccess: async (id) => {
      await queryClient.invalidateQueries({ queryKey: ["reservations"] });
      navigate(`/reservations/${id}`);
    },
  });
  if (!authenticated)
    return (
      <div className="page">
        <PageHeader
          back={() => navigate(-1)}
          title="Sign in to request bookings"
        />
        <Button
          onClick={() =>
            navigate(
              `/auth?returnTo=${encodeURIComponent(`/reserve/${planId}`)}`,
            )
          }
        >
          Sign in
        </Button>
      </div>
    );
  const requestStages =
    plan?.stages.filter((stage) => stage.bookingMode === "request_to_book") ??
    [];
  const itineraryStages =
    plan?.stages.filter(
      (stage) =>
        stage.bookingMode === "walk_in" ||
        stage.bookingMode === "no_booking_required",
    ) ?? [];
  return (
    <div className="page">
      <PageHeader
        back={() => navigate(-1)}
        eyebrow="BOOKING REQUESTS"
        title="Confirm your details"
      />
      <div className="notice">
        <strong>{t.reservations.independent}</strong>
        <p>{t.reservations.independentBody}</p>
      </div>
      {itineraryStages.length > 0 && (
        <section className="itinerary-summary">
          <h2>Already in your itinerary</h2>
          {itineraryStages.map((stage) => (
            <div className="row row--between" key={stage.id}>
              <span>{stage.offeringName}</span>
              <Tag tone="success">
                {stage.bookingMode === "walk_in"
                  ? t.booking.walk_in
                  : t.booking.no_booking_required}
              </Tag>
            </div>
          ))}
        </section>
      )}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          analytics.track("booking_requests_started", {
            requestCount: requestStages.length,
          });
          mutation.mutate();
        }}
      >
        <label>
          Lead guest name
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoComplete="name"
            required
          />
        </label>
        <label>
          Verified mobile number
          <input value="+961 71 234 567" disabled />
          <small>Verified</small>
        </label>
        <label>
          Group size
          <input
            type="number"
            min="1"
            max="20"
            defaultValue={draft.adults + draft.children}
          />
        </label>
        <label>
          Occasion (optional)
          <select>
            <option value="">No occasion</option>
            <option>Birthday</option>
            <option>Anniversary</option>
            <option>Celebration</option>
          </select>
        </label>
        <label>
          Special requests (optional)
          <textarea
            maxLength={500}
            placeholder="Dietary needs, accessibility or seating requests"
          />
        </label>
        <Button
          type="submit"
          disabled={requestStages.length === 0 || mutation.isPending}
        >
          {mutation.isPending
            ? "Sending requests…"
            : `Send ${requestStages.length} booking ${requestStages.length === 1 ? "request" : "requests"}`}
        </Button>
        {mutation.isError && (
          <p className="field-error" role="alert">
            {mutation.error instanceof Error
              ? mutation.error.message
              : "Could not send booking requests"}
          </p>
        )}
        <p className="legal-copy">
          No deposit is charged until an individual place accepts. You’ll review
          each charge first.
        </p>
      </form>
    </div>
  );
}
